interface Props {
  open: boolean
  onClose: () => void
  onSelectMode: (mode: 'simple' | 'advanced' | 'merge' | 'multiMerge') => void
}

export default function ModeSelectModal({ open, onClose, onSelectMode }: Props) {
  if (!open) return null
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[560px] overflow-hidden"
        onClick={e => e.stopPropagation()}>

        <div className="px-5 py-4 flex items-center justify-between" style={{ background: '#1a2942' }}>
          <div>
            <h2 className="text-[15px] font-bold text-white">등록 방식 선택</h2>
            <p className="text-[11px] text-slate-400 mt-0.5">3D Mesh 데이터를 등록하는 방식을 선택하세요</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition p-1 text-lg leading-none">✕</button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <button
            onClick={() => onSelectMode('simple')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-green-500 hover:bg-green-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">간편 등록</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">파일과 기본 정보만으로 빠르게 등록합니다. 좌표계와 출력 옵션은 자동으로 추천됩니다.</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {['파일 업로드', '제목', '좌표계', '추천 옵션'].map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t}</span>
              ))}
            </div>
          </button>

          <button
            onClick={() => onSelectMode('advanced')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-blue-500 hover:bg-blue-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">상세 옵션 등록</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">mago-3d-tiler의 모든 변환 옵션을 직접 설정하여 정밀하게 제어합니다.</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {['LOD 설정', 'Proj4', 'Up-Axis', '곡률 보정', '오프셋'].map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t}</span>
              ))}
            </div>
            <span className="text-[10px] text-blue-600 font-bold">전문가 모드 →</span>
          </button>

          <button
            onClick={() => onSelectMode('merge')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-slate-500 hover:bg-slate-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v12a2 2 0 002 2h10M18 15l-4-4 4-4"/><path d="M8 5H4a2 2 0 00-2 2v10a2 2 0 002 2h4"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">기존 타일셑 병합</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">이미 변환이 완료된 데이터 여러 개를 골라 재변환 없이 하나의 타일셋으로 묶습니다.</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {['변환 완료 데이터 선택', '재변환 없음'].map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t}</span>
              ))}
            </div>
          </button>

          <button
            onClick={() => onSelectMode('multiMerge')}
            className="group p-5 rounded-xl border-2 border-slate-200 text-left flex flex-col gap-3 hover:border-slate-500 hover:bg-slate-50 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">신규 타일셑 병합</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">아직 변환하지 않은 원본 파일 여러 개를 올려 순차 변환한 뒤 하나의 타일셋으로 병합합니다.</p>
            </div>
            <div className="flex flex-wrap gap-1 mt-auto">
              {['다중 파일 업로드', '순차 변환', 'mago-3d-tiler merge'].map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">{t}</span>
              ))}
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}
